import { fetchCurrentUser } from '../../api/me';
import { getApiToken } from '../../api/authToken';
import { upsertAppointment } from '../../api/appointments';
import * as msal from '@azure/msal-browser';
import { getMsalConfig } from './msalConfig';
import { parseAppointmentDate } from '../../shared/calendar-sync/parseAppointmentDate';

async function getUserCalendarIntegrations() {
  const token = await getApiToken();
  const payload = await fetchCurrentUser(token);
  return payload?.user?.calendarIntegrations || {};
}

function ensureDate(value, label) {
  const date = value instanceof Date ? value : parseAppointmentDate(value);
  if (!date) {
    throw new Error(`Invalid ${label}`);
  }
  return date;
}

export const syncWithOutlookCalendar = async (appointment, userId, skipAuth = false) => {
  try {
    const integrations = await getUserCalendarIntegrations();
    if (!integrations.outlook) {
      throw new Error('Outlook Calendar integration is not enabled');
    }

    const msalInstance = new msal.PublicClientApplication(getMsalConfig());
    await msalInstance.initialize();

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0 && !skipAuth) {
      throw new Error('No authenticated account found');
    }

    const tokenRequest = {
      scopes: ['Calendars.ReadWrite'],
      account: accounts[0],
    };

    const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest).catch(async (error) => {
      if (error instanceof msal.InteractionRequiredAuthError && !skipAuth) {
        return await msalInstance.acquireTokenPopup(tokenRequest);
      }
      throw error;
    });

    const startDate = ensureDate(appointment.dateTime, 'start date');
    const endDate = ensureDate(appointment.endTime, 'end date');

    const event = {
      subject: `Appointment with ${appointment.clientName}`,
      body: {
        contentType: 'HTML',
        content: appointment.notes || '',
      },
      start: {
        dateTime: startDate.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: endDate.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      isReminderOn: true,
    };

    let response;
    if (appointment.isUpdate && (appointment.outlookEventId || appointment.previousOutlookEventId)) {
      const eventIdsToDelete = [appointment.outlookEventId, appointment.previousOutlookEventId].filter(Boolean);

      for (const eventId of eventIdsToDelete) {
        try {
          await fetch(`https://graph.microsoft.com/v1.0/me/events/${eventId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
          });
        } catch {
          // Non-fatal: continue with create flow
        }
      }

      response = await fetch('https://graph.microsoft.com/v1.0/me/events', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      });
    } else {
      response = await fetch('https://graph.microsoft.com/v1.0/me/events', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      });
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Outlook API error: ${errorData.error?.message || response.statusText}`);
    }

    const result = await response.json();

    return {
      success: true,
      eventId: result.id,
      wasUpdated: appointment.isUpdate && appointment.outlookEventId,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Unknown error occurred during Outlook sync',
    };
  }
};

export const syncExistingOutlookAppointments = async (appointments, userId, companyId = null) => {
  try {
    const integrations = await getUserCalendarIntegrations();
    if (!integrations.outlook) {
      throw new Error('Outlook Calendar integration is not enabled');
    }

    const results = [];

    for (const appointment of appointments) {
      if (appointment.outlookEventId) continue;

      const result = await syncWithOutlookCalendar(
        {
          ...appointment,
          dateTime: appointment.dateTime,
          endTime: appointment.endTime,
          clientName: appointment.clientName,
          isUpdate: false,
        },
        userId,
        true
      );

      if (result.success && result.eventId && companyId && appointment.id) {
        await upsertAppointment({
          companyId,
          id: appointment.id,
          appointment: { outlookEventId: result.eventId },
        });
      }

      results.push({
        appointmentId: appointment.id,
        ...result,
      });
    }

    return {
      success: true,
      results,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};
